import { Fragment, useMemo } from "react";
import type { KeyboardEvent } from "react";

import type { TextConfig } from "../../blockTypes";

/**
 * TextBlock
 *
 * Renders text using the same inline tag syntax (and parsing rules) as the
 * Android CustomTextView:
 *   <b>bold</b>
 *   <h>highlighted chip</h>
 *   <c>copyable code chip</c>
 */

type TagType = "b" | "h" | "c";

interface TextPart {
    type: "text" | TagType;
    content: string;
}

// Unicode isolates: keep operators such as "<=" in LTR order inside RTL text
const LRI = "\u2066";
const PDI = "\u2069";

// Tags that get a trailing space (code chips don't, same as Android)
const SPACE_AFTER: Record<TagType, boolean> = { b: true, h: true, c: false };

const CHIP_CLASSES = "inline-block rounded-[10px] bg-gray-200 px-2 py-1 text-black";

// Same as Java's Character.isWhitespace: non-breaking spaces don't count
const isWhitespace = (char: string): boolean =>
    /\s/.test(char) && !/[\u00A0\u2007\u202F]/.test(char);

// Latin letters and operators need an English locale to render correctly
const needsEnglish = (text: string): boolean => /[a-zA-Z<>=!]/.test(text);

/**
 * Equivalent of Android's normalizeText():
 * Html.fromHtml(...) collapses whitespace, drops the leading space of a
 * segment, turns <br> into a newline, strips other tags and decodes entities.
 */
const normalizeText = (text: string): string => {
    const collapsed = text
        .split(/<br\s*\/?>/i)
        .map((chunk) => chunk.replace(/[ \t\r\n]+/g, " ").replace(/^ /, ""))
        .join("\n");

    // DOMParser never runs scripts, and the result is rendered as plain text
    const decoded =
        new DOMParser().parseFromString(collapsed, "text/html").body.textContent ?? "";

    // Fix the not-equal operator if it was stored as "=!"
    return decoded.replace(/=!/g, "!=");
};

// Wrap every comparison operator in an isolate (single pass, longest first)
const protectOperators = (text: string): string =>
    text.replace(/<=|>=|==|!=|<|>/g, (operator) => `${LRI}${operator}${PDI}`);

const prepareText = (text: string): string => protectOperators(normalizeText(text));

/**
 * Splits the input into plain text and tagged parts, inserting the same
 * spaces Android adds around tags.
 */
const parseText = (input: string): TextPart[] => {
    const parts: TextPart[] = [];
    let lastChar = ""; // Last character emitted so far

    const push = (type: TextPart["type"], content: string) => {
        if (!content) return;
        parts.push({ type, content });
        lastChar = content.slice(-1);
    };

    const pushTag = (type: TagType, rawContent: string) => {
        const content = prepareText(rawContent);
        if (!content) return;

        // Don't let the tag stick to the previous word
        if (lastChar && !isWhitespace(lastChar)) push("text", " ");

        push(type, content);

        if (SPACE_AFTER[type]) push("text", " ");
    };

    const tagRegex = /<(b|h|c)>(.*?)<\/\1>/gs;
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = tagRegex.exec(input)) !== null) {
        // Normal text before the tag
        push("text", prepareText(input.slice(lastIndex, match.index)));
        pushTag(match[1] as TagType, match[2]);
        lastIndex = tagRegex.lastIndex;
    }

    // Remaining normal text
    push("text", prepareText(input.slice(lastIndex)));

    return parts;
};

// Copies code to the clipboard without the invisible BiDi characters
const copyCode = (content: string) => {
    const plainText = content.replace(/[\u2066\u2069]/g, "");
    navigator.clipboard?.writeText(plainText).catch(() => undefined);
};

const renderPart = (part: TextPart, index: number) => {
    const lang = needsEnglish(part.content) ? "en" : undefined;

    switch (part.type) {
        case "b":
            return <strong key={index}>{part.content}</strong>;

        case "h":
            return (
                <span key={index} lang={lang} className={CHIP_CLASSES}>
                    {part.content}
                </span>
            );

        case "c":
            return (
                // Works with mouse and keyboard (Enter / Space)
                <code
                    key={index}
                    lang={lang}
                    role="button"
                    tabIndex={0}
                    className={`${CHIP_CLASSES} font-mono cursor-pointer`}
                    onClick={() => copyCode(part.content)}
                    onKeyDown={(event: KeyboardEvent<HTMLElement>) => {
                        if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            copyCode(part.content);
                        }
                    }}
                >
                    {part.content}
                </code>
            );

        default:
            return <Fragment key={index}>{part.content}</Fragment>;
    }
};

interface TextBlockProps {
    config?: TextConfig;
}

const TextBlock = ({ config }: TextBlockProps) => {
    const text = config?.text ?? "";
    const parts = useMemo(() => parseText(text), [text]);

    // whitespace-pre-line: whitespace is already collapsed by the parser,
    // so only the newlines coming from <br> are preserved
    return (
        <div
            dir="rtl"
            className="text-base leading-[1.35] text-gray-800 text-right whitespace-pre-line"
        >
            {parts.map(renderPart)}
        </div>
    );
};

export default TextBlock;